import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import CustomerNav from './CustomerNav';
import { ArrowLeft, Clock, CheckCircle, XCircle, User, Mail, Phone, MapPin, FileText, AlertCircle } from 'lucide-react';
import { getServiceLineGross, getOfferTotals, getOfferSalePercent, formatSalePercent, shouldShowLinePrices, shouldShowSubtotals, shouldShowTotals, formatDisplayAmount, formatOfferTotalText } from '../utils/offerLineDisplay';

function formatAddress(customerInfo) {
  if (!customerInfo) return '';
  const { street, city, zip, country, address } = customerInfo;
  if (street || city || zip) {
    const parts = [street].filter(Boolean);
    if (zip || city) parts.push([zip, city].filter(Boolean).join(' '));
    if (country) parts.push(country);
    return parts.join(', ');
  }
  return address || '';
}

const CustomerOfferDetail = () => {
  const { t } = useTranslation();
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, logout } = useCustomerAuth();
  const [offer, setOffer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchOffer();
  }, [offerId]);

  const fetchOffer = async () => {
    try {
      const response = await fetch(`/api/customer/offers/${offerId}`, {
        headers: getAuthHeaders()
      });

      if (response.status === 401) {
        logout();
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setOffer(data.offer);
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('customer.offerDetail.failedToLoadOffer'));
      }
    } catch (error) {
      console.error('Error fetching offer:', error);
      setError(t('customer.offerDetail.failedToLoadOffer'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccept = async () => {
    console.log('handleAccept called', { offerId });

    setIsProcessing(true);
    setError('');
    setShowAcceptModal(false);

    try {
      const authHeaders = getAuthHeaders();
      console.log('Making API request to accept offer', { offerId, hasAuthHeaders: !!authHeaders.Authorization });
      
      const response = await fetch(`/api/customer/offers/${offerId}/accept`, {
        method: 'PUT',
        headers: authHeaders
      });

      console.log('API response received', { status: response.status, ok: response.ok });

      if (response.status === 401) {
        console.log('Unauthorized - logging out');
        setIsProcessing(false);
        logout();
        return;
      }

      if (response.ok) {
        const responseData = await response.json();
        console.log('Offer accepted successfully', responseData);
        await fetchOffer(); // Refresh offer data
        alert(t('customer.offerDetail.offerSubmittedSuccess'));
      } else {
        // Handle error response - try to parse JSON, but handle non-JSON responses
        let errorMessage = t('customer.offerDetail.failedToLoadOffer');
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
          console.error('API error response', errorData);
        } catch (jsonError) {
          // Response is not JSON, try to get text
          try {
            const textError = await response.text();
            console.error('Non-JSON error response', textError);
            errorMessage = textError || errorMessage;
          } catch (textError) {
            console.error('Failed to parse error response', textError);
          }
        }
        setError(errorMessage);
        console.error('Failed to submit offer', { status: response.status, errorMessage });
      }
    } catch (error) {
      console.error('Error submitting offer:', error);
      const errorMessage = error.message || t('customer.offerDetail.failedToSubmitOffer');
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
      console.log('handleAccept completed');
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert(t('customer.offerDetail.provideRejectionReason'));
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      const authHeaders = getAuthHeaders();
      console.log('Making API request to reject offer', { offerId, hasAuthHeaders: !!authHeaders.Authorization });
      
      const response = await fetch(`/api/customer/offers/${offerId}/reject`, {
        method: 'PUT',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: rejectionReason })
      });

      console.log('API response received', { status: response.status, ok: response.ok });

      if (response.status === 401) {
        console.log('Unauthorized - logging out');
        setIsProcessing(false);
        logout();
        return;
      }

      if (response.ok) {
        const responseData = await response.json();
        console.log('Offer rejected successfully', responseData);
        await fetchOffer(); // Refresh offer data
        setShowRejectModal(false);
        setRejectionReason('');
        alert(t('customer.offerDetail.offerRejectedSuccess'));
      } else {
        // Handle error response - try to parse JSON, but handle non-JSON responses
        let errorMessage = t('customer.offerDetail.failedToLoadOffer');
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
          console.error('API error response', errorData);
        } catch (jsonError) {
          // Response is not JSON, try to get text
          try {
            const textError = await response.text();
            console.error('Non-JSON error response', textError);
            errorMessage = textError || errorMessage;
          } catch (textError) {
            console.error('Failed to parse error response', textError);
          }
        }
        setError(errorMessage);
        console.error('Failed to reject offer', { status: response.status, errorMessage });
      }
    } catch (error) {
      console.error('Error rejecting offer:', error);
      const errorMessage = error.message || t('customer.offerDetail.failedToRejectOffer');
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
      console.log('handleReject completed');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: (
        <span className="px-3 py-1 text-sm font-semibold rounded-full bg-yellow-100 text-yellow-800 flex items-center gap-2">
          <Clock className="w-4 h-4" />
          {t('customer.status.pending')}
        </span>
      ),
      accepted: (
        <span className="px-3 py-1 text-sm font-semibold rounded-full bg-green-100 text-green-800 flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />
          {t('customer.status.accepted')}
        </span>
      ),
      rejected: (
        <span className="px-3 py-1 text-sm font-semibold rounded-full bg-red-100 text-red-800 flex items-center gap-2">
          <XCircle className="w-4 h-4" />
          {t('customer.status.rejected')}
        </span>
      )
    };
    return badges[status] || badges.pending;
  };

  const formatAmount = (amount, offerContext = offer) => {
    const formatted = formatDisplayAmount(amount, offerContext);
    if (formatted === null) {
      return offerContext?.displayMode === 'hidden' ? t('offer.pricesHidden') : 'N/A';
    }
    return formatted;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CustomerNav />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            <p className="mt-4 text-gray-600">{t('customer.offerDetail.loadingOffer')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CustomerNav />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('common.error')}</h2>
            <p className="text-gray-600 mb-4">{error || t('customer.offerDetail.offerNotFound')}</p>
            <button
              onClick={() => navigate('/customer/offers')}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              {t('customer.offerDetail.backToOffers')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { subtotalNet, totalVat } = getOfferTotals(offer);
  const salePercent = getOfferSalePercent(offer);
  const showLinePrices = shouldShowLinePrices(offer);
  const showTotals = shouldShowTotals(offer);
  const showSubtotals = shouldShowSubtotals(offer);
  const totalDisplay = formatOfferTotalText(offer);

  return (
    <div className="min-h-screen bg-gray-50">
      <CustomerNav />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/customer/offers')}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('customer.offerDetail.backToOffers')}
        </button>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" role="alert">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-800 font-medium">{error}</p>
            </div>
            <button
              type="button"
              onClick={() => setError('')}
              className="text-red-600 hover:text-red-800 flex-shrink-0"
              aria-label="Dismiss error"
            >
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('customer.offerDetail.offerDetails')}</h1>
              <p className="text-sm text-gray-500 mt-1">
                {t('customer.offerDetail.created', { date: new Date(offer.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) })}
              </p>
            </div>
            {getStatusBadge(offer.status)}
          </div>

          <div className="px-6 py-6 space-y-6">
            {/* Offer Details */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {t('customer.offerDetail.offerInformation')}
              </h2>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700 whitespace-pre-wrap">{offer.offerDetails}</p>
              </div>
            </div>

            {/* Services */}
            {offer.services && offer.services.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('customer.offerDetail.services')}</h2>
                <div className="space-y-2">
                  {offer.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{service.name}</p>
                        {service.description && (
                          <p className="text-sm text-gray-600 mt-1">{service.description}</p>
                        )}
                      </div>
                      {showLinePrices && service.price !== undefined && (
                        <p className="font-semibold text-gray-900 text-right">
                          €{getServiceLineGross(service).toFixed(2)}
                          {service.lineVat > 0 && (
                            <span className="block text-xs font-normal text-gray-500">
                              {t('offer.lineNetVat', {
                                net: Number(service.lineNet).toFixed(2),
                                vat: Number(service.lineVat).toFixed(2)
                              })}
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Total Amount */}
            {showTotals && (totalDisplay || offer.displayMode === 'hidden') && (
              <div className="border-t border-gray-200 pt-4 space-y-2">
                {showSubtotals && subtotalNet != null && totalVat != null && totalVat > 0 && (
                  <>
                    <div className="flex items-center justify-between text-sm text-gray-700">
                      <span>{t('offer.subtotalNet')}</span>
                      <span>{formatAmount(subtotalNet)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-700">
                      <span>{t('offer.totalVat')}</span>
                      <span>{formatAmount(totalVat)}</span>
                    </div>
                  </>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-gray-900">
                    {offer.displayMode === 'range'
                      ? t('offer.estimatedTotal')
                      : totalVat > 0
                        ? t('offer.totalGross')
                        : t('customer.offerDetail.totalAmount')}
                  </span>
                  <span className="text-2xl font-bold text-indigo-600">
                    {totalDisplay || t('offer.pricesHidden')}
                  </span>
                </div>
                {salePercent > 0 && (
                  <p className="text-sm font-medium text-indigo-700">
                    {t('offer.saleApplied', { percent: formatSalePercent(salePercent) })}
                  </p>
                )}
                {offer.validUntil && (
                  <p className="text-sm text-gray-500 mt-2">
                    {t('customer.offerDetail.validUntil', { date: new Date(offer.validUntil).toLocaleDateString() })}
                  </p>
                )}
              </div>
            )}
            {!showTotals && salePercent > 0 && (
              <p className="text-sm font-medium text-indigo-700 border-t border-gray-200 pt-4">
                {t('offer.saleApplied', { percent: formatSalePercent(salePercent) })}
              </p>
            )}

            {/* Customer Information */}
            {offer.customerInfo && Object.keys(offer.customerInfo).length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <User className="w-5 h-5" />
                  {t('customer.offerDetail.customerInformation')}
                </h2>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  {offer.customerInfo.name && (
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-700">{offer.customerInfo.name}</span>
                    </div>
                  )}
                  {offer.customerInfo.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-700">{offer.customerInfo.email}</span>
                    </div>
                  )}
                  {offer.customerInfo.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-700">{offer.customerInfo.phone}</span>
                    </div>
                  )}
                  {(() => {
                    const addressLine = formatAddress(offer.customerInfo);
                    return addressLine ? (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-700">{addressLine}</span>
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>
            )}

            {/* Status Information */}
            {offer.status !== 'pending' && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('customer.offerDetail.statusInformation')}</h2>
                <div className="bg-gray-50 rounded-lg p-4">
                  {offer.status === 'accepted' && offer.acceptedAt && (
                    <p className="text-gray-700">
                      <span className="font-medium">{t('customer.offerDetail.acceptedOn')}</span>{' '}
                      {new Date(offer.acceptedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  )}
                  {offer.status === 'rejected' && offer.rejectedAt && (
                    <div>
                      <p className="text-gray-700 mb-2">
                        <span className="font-medium">{t('customer.offerDetail.rejectedOn')}</span>{' '}
                        {new Date(offer.rejectedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      {offer.rejectionReason && (
                        <p className="text-gray-700">
                          <span className="font-medium">{t('customer.offerDetail.reason')}</span> {offer.rejectionReason}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Actions - Only show for 'pending' offers (after customer accepted) */}
            {offer.status === 'pending' && (
              <div className="border-t border-gray-200 pt-6">
                <p className="text-sm text-gray-600 mb-4">
                  {t('customer.offerDetail.pendingDecision')}
                </p>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowAcceptModal(true);
                    }}
                    disabled={isProcessing}
                    className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="w-5 h-5" />
                    {t('customer.offerDetail.submitOffer')}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowRejectModal(true);
                    }}
                    disabled={isProcessing}
                    className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <XCircle className="w-5 h-5" />
                    {t('customer.offerDetail.rejectOffer')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Accept Confirmation Modal */}
        {showAcceptModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                {t('customer.offerDetail.submitOffer')}
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                {t('customer.offerDetail.confirmSubmitMessage')}
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowAcceptModal(false);
                  }}
                  disabled={isProcessing}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAccept();
                  }}
                  disabled={isProcessing}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {t('customer.offerDetail.processing')}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      {t('customer.offerDetail.confirmSubmit')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reject Modal */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('customer.offerDetail.rejectOffer')}</h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('customer.offerDetail.rejectReasonPrompt')}
              </p>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder={t('customer.offerDetail.rejectionPlaceholder')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                rows="4"
              />
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowRejectModal(false);
                    setRejectionReason('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleReject();
                  }}
                  disabled={isProcessing || !rejectionReason.trim()}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? t('customer.offerDetail.processing') : t('customer.offerDetail.rejectOffer')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerOfferDetail;
